import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { 
  RESOURCE_LEVEL_PERMISSIONS, 
  RESOURCE_TYPES, 
  WRITE_PERMISSIONS, 
  READ_PERMISSIONS,
  PERMISSION_DESCRIPTIONS 
} from '../../../types/permissions';




const PermissionModal = ({ 
  visible, 
  onCancel, 
  onSubmit, 
  projects, 
  resources, 
  initialValues = null
}) => {
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedResource, setSelectedResource] = useState('');
  const [permissionLevel, setPermissionLevel] = useState('');
  const [specificPermissions, setSpecificPermissions] = useState([]);
  const [availableResources, setAvailableResources] = useState([]);
  useEffect(() => {
    if (visible && initialValues) {
      setSelectedProject(initialValues.projectId || '');
      setSelectedResource(initialValues.resourceId || '');
      setPermissionLevel(Array.isArray(initialValues.permissions) 
        ? RESOURCE_LEVEL_PERMISSIONS.WRITE 
        : initialValues.permissions);
      setSpecificPermissions(Array.isArray(initialValues.permissions) 
        ? initialValues.permissions 
        : []);
    } else if (visible) {
      setSelectedProject('');
      setSelectedResource('');
      setPermissionLevel('');
      setSpecificPermissions([]);
    }
  }, [visible, initialValues]);

  useEffect(() => {
    if (selectedProject) {
      const filteredResources = resources.filter(
        r => r.projectId === selectedProject
      );
      setAvailableResources(filteredResources);
      if (!filteredResources.some(r => r.id === selectedResource)) {
        setSelectedResource('');
      }
    } else {
      setAvailableResources([]);
      setSelectedResource('');
    }
  }, [selectedProject, resources]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedProject || !selectedResource) return;
    if (!permissionLevel) return;
    if (permissionLevel === RESOURCE_LEVEL_PERMISSIONS.WRITE && specificPermissions.length === 0) return;

    const permissions = permissionLevel === RESOURCE_LEVEL_PERMISSIONS.WRITE 
      ? specificPermissions 
      : permissionLevel;
    
    const payload = {
      projectId: selectedProject,
      resourceId: selectedResource,
      permissions
    };

    onSubmit(payload);
  };

  const handleCancel = () => {
    setSelectedProject('');
    setSelectedResource('');
    setPermissionLevel('');
    setSpecificPermissions([]);
    onCancel();
  };

  const toggleSpecificPermission = (permission) => {
    setSpecificPermissions(prev => 
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  if (!visible) return null;

  const selectedResourceType = availableResources.find(r => r.id === selectedResource)?.type;
  const showSpecificPermissions = permissionLevel === RESOURCE_LEVEL_PERMISSIONS.WRITE && selectedResourceType;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          <div className="bg-gray-900 px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg font-medium text-white">
              {initialValues ? "Edit Permissions" : "Add Permissions"}
            </h3>
            <button
              onClick={handleCancel}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project
                </label>
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 bg-white shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a project</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}-{project.environment}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resource
                </label>
                <select
                  value={selectedResource}
                  onChange={(e) => setSelectedResource(e.target.value)}
                  disabled={!selectedProject}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 bg-white shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a resource</option>
                  {availableResources.map(resource => (
                    <option key={resource.id} value={resource.id}>
                      {resource.name} ({RESOURCE_TYPES[resource.type]})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Permission Level
                </label>
                <select
                  value={permissionLevel}
                  onChange={(e) => {
                    setPermissionLevel(e.target.value);
                    if (e.target.value !== RESOURCE_LEVEL_PERMISSIONS.WRITE) {
                      setSpecificPermissions([]);
                    }
                  }}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 bg-white shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  required
                >
                  <option value="">Select permission level</option>
                  {Object.values(RESOURCE_LEVEL_PERMISSIONS).map(perm => (
                    <option key={perm} value={perm}>
                      {perm} - {PERMISSION_DESCRIPTIONS[perm]}
                    </option>
                  ))}
                </select>
              </div>

              {showSpecificPermissions && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specific Permissions
                  </label>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    {WRITE_PERMISSIONS[selectedResourceType]?.map(perm => (
                      <div 
                        key={perm}
                        className="flex items-center justify-between hover:bg-white rounded-md p-2 transition-colors cursor-pointer"
                        onClick={() => toggleSpecificPermission(perm)}
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900">{perm}</p>
                          <p className="text-sm text-gray-500">{PERMISSION_DESCRIPTIONS[perm]}</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={specificPermissions.includes(perm)}
                          onChange={() => {}}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  {initialValues ? 'Update' : 'Add'} Permissions
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionModal;